import { useCallback, useEffect, useState } from "react";
import {
  createFileRoute,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { getIdToken } from "firebase/auth";
import { auth } from "@/auth/firebase";
import GoogleLogo from "@/assets/icons/google-logo.svg";
import MicrosoftLogo from "@/assets/icons/microsoft-logo.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ErrorText from "@/components/universal/error-text";
import {
  handleCredentialSignIn,
  handleGoogleSignIn,
  handleMicrosoftSignIn,
  usePendingCredential,
} from "@/routes/auth/-auth-functions";
import { getBaseUrl } from "@/requests/axios";
import type { BodyFirebaseCallback } from "@/requests/gen/react-query/fastAPI.schemas";
import type { JSX } from "react";

const mcpLoginSearchSchema = z.object({
  state: z.string().optional(),
  continue: z.string().optional(),
});

export const Route = createFileRoute("/_public/mcp-login")({
  validateSearch: mcpLoginSearchSchema,
  component: McpLoginPage,
});

interface LoginForm {
  email: string;
  password: string;
}

/**
 * MCP OAuth sign-in page.
 *
 * Claude redirects users here as part of the OAuth 2.1 flow. After Firebase
 * sign-in, we get a Firebase ID token and submit it to the backend via a
 * native form POST. The backend verifies the token, creates an auth code,
 * and redirects the browser back to Claude's callback URL automatically.
 */
function McpLoginPage(): JSX.Element {
  const { state = "", continue: callbackUrl = "" } = Route.useSearch();
  const location = useLocation();
  const callbackUrlIsValid = callbackUrl.startsWith(getBaseUrl());
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<LoginForm>({
    defaultValues: { email: "", password: "" },
  });

  const {
    set: setPendingCredential,
    get: getPendingCredential,
    clear: clearPendingCredential,
  } = usePendingCredential();

  const navigate = useNavigate();
  const onMfaRequired = () =>
    navigate({
      to: "/auth/mfa-challenge",
      search: { next: location.pathname + location.searchStr },
    });

  const submitToBackend = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setError(
        "Sign-in succeeded but no user session found. Please try again.",
      );
      return;
    }

    setIsRedirecting(true);
    setError(null);

    const idToken = await getIdToken(user);

    // Submit a native form POST so the browser follows the backend's 302
    // redirect back to Claude's callback URL automatically.
    const payload: BodyFirebaseCallback = { firebase_id_token: idToken, state };

    const nativeForm = document.createElement("form");
    nativeForm.method = "POST";
    nativeForm.action = callbackUrl;

    for (const [name, value] of Object.entries(payload)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      nativeForm.appendChild(input);
    }
    document.body.appendChild(nativeForm);
    nativeForm.submit();
  }, [callbackUrl, state]);

  useEffect(() => {
    if (!callbackUrlIsValid || isRedirecting || !auth.currentUser) return;
    void submitToBackend();
  }, [callbackUrlIsValid, isRedirecting, submitToBackend]);

  async function onSubmit(formData: LoginForm) {
    const result = await handleCredentialSignIn({
      form,
      email: formData.email,
      password: formData.password,
      getPendingCredential,
      clearPendingCredential,
      onMfaRequired,
    });
    if (result === "success") {
      await submitToBackend();
    }
  }

  async function onGoogleSignIn() {
    const result = await handleGoogleSignIn({
      form,
      getPendingCredential,
      clearPendingCredential,
      onMfaRequired,
    });
    if (result === "success") {
      await submitToBackend();
    }
  }

  async function onMicrosoftSignIn() {
    const result = await handleMicrosoftSignIn({
      form,
      setPendingCredential,
      onMfaRequired,
    });
    if (result === "success") {
      await submitToBackend();
    }
  }

  // Ensure the callback URL is valid before rendering the page, to prevent phishing attacks.
  if (!callbackUrl || !callbackUrlIsValid) {
    return (
      <div className="flex w-full max-w-sm flex-col gap-6">
        <title>Invalid Redirect URL - Legrande Health Portal</title>
        <Card className="sm:max-w-96 w-full text-center">
          <CardHeader>
            <CardTitle>Invalid Redirect URL</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm py-4">
              The redirect URL is missing or invalid. Please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-6">
      <title>Connect Claude - Legrande Health Portal</title>
      <Card className="sm:max-w-96 w-full text-center">
        <CardHeader>
          <CardTitle>Connect Claude</CardTitle>
          <CardDescription>
            Sign in to authorize Claude to access the NOMP API
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isRedirecting ? (
            <p className="text-muted-foreground text-sm py-4">
              Redirecting back to Claude...
            </p>
          ) : (
            <>
              <div className="flex flex-col items-center justify-center gap-4">
                <Button
                  onClick={onGoogleSignIn}
                  variant="outline"
                  className="w-full text-md font-normal"
                >
                  <img
                    className="size-6 mr-2"
                    src={GoogleLogo}
                    loading="lazy"
                    alt="google logo"
                  />
                  Sign in with Google
                </Button>
                <Button
                  onClick={onMicrosoftSignIn}
                  variant="outline"
                  className="w-full text-md font-normal"
                >
                  <img
                    className="size-6 mr-2"
                    src={MicrosoftLogo}
                    loading="lazy"
                    alt="microsoft logo"
                  />
                  Sign in with Microsoft
                </Button>
              </div>
              <div className="flex items-center justify-center gap-4 mt-4">
                <Separator className="my-4 flex-1" />
                <span className="text-muted-foreground text-sm">or</span>
                <Separator className="my-4 flex-1" />
              </div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="text-left space-y-4"
                >
                  <FormField
                    control={form.control}
                    rules={{ required: "Email is required" }}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="text" autoComplete="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    rules={{ required: "Password is required" }}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="password"
                            autoComplete="current-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                  {form.formState.errors.root?.serverError && (
                    <ErrorText
                      text={
                        form.formState.errors.root.serverError.message ||
                        "Error signing in. Please try again."
                      }
                    />
                  )}
                </form>
              </Form>
              {error && <ErrorText text={error} />}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
